import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Grid, CircularProgress, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import StoreIcon from '@mui/icons-material/Store';
import CategoryIcon from '@mui/icons-material/Category';
import InventoryIcon from '@mui/icons-material/Inventory';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import WarningIcon from '@mui/icons-material/Warning';
import KpiCard from './KpiCard';
import { DashboardService } from '../../services/dashboard.service';
import { AuthService } from '../../services/auth.service';
import { StoreAdminDashboard as StoreAdminDashboardData } from '../../interfaces/dashboard.interface';

const StoreAdminDashboard: React.FC = () => {
    const [data, setData] = useState<StoreAdminDashboardData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchDashboard = async () => {
            try {
                const institutionId = AuthService.getSelectedInstitution();
                const result = await DashboardService.getStoreAdminDashboard(institutionId);
                setData(result);
            } catch (err: any) {
                setError(err.response?.data?.message || 'Failed to load dashboard data');
            } finally {
                setLoading(false);
            }
        };
        fetchDashboard();
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
    }

    if (!data) return null;

    return (
        <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
                Store Dashboard
            </Typography>

            {/* Stock Overview */}
            <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                Stock Overview
            </Typography>
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <KpiCard
                        title="Total Stores"
                        value={data.totalStores}
                        icon={<StoreIcon fontSize="inherit" />}
                        color="#1976d2"
                        linkTo="/stores"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <KpiCard
                        title="Unique Items"
                        value={data.totalUniqueItems}
                        icon={<CategoryIcon fontSize="inherit" />}
                        color="#7b1fa2"
                        linkTo="/items"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <KpiCard
                        title="Total Stock Qty"
                        value={data.totalStockQuantity}
                        icon={<InventoryIcon fontSize="inherit" />}
                        color="#2e7d32"
                        linkTo="/stocks"
                    />
                </Grid>
            </Grid>

            {/* Today's Movements */}
            <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
                Today's Movements
            </Typography>
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <KpiCard
                        title="Today's Stock In"
                        value={data.todayStockIn}
                        icon={<ArrowDownwardIcon fontSize="inherit" />}
                        color="#00838f"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <KpiCard
                        title="Today's Stock Out"
                        value={data.todayStockOut}
                        icon={<ArrowUpwardIcon fontSize="inherit" />}
                        color="#e65100"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <KpiCard
                        title="Low Stock Items"
                        value={data.lowStockItemCount}
                        icon={<WarningIcon fontSize="inherit" />}
                        color="#d32f2f"
                    />
                </Grid>
            </Grid>

            {/* Low Stock Items Table */}
            {data.lowStockItems.length > 0 && (
                <>
                    <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
                        Low Stock Items
                    </Typography>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Store</strong></TableCell>
                                    <TableCell><strong>Item</strong></TableCell>
                                    <TableCell align="right"><strong>Available Qty</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.lowStockItems.map((item, index) => (
                                    <TableRow
                                        key={index}
                                        sx={{
                                            backgroundColor: item.availableQty === 0 ? '#ffebee' : 'inherit',
                                        }}
                                    >
                                        <TableCell>{item.storeName}</TableCell>
                                        <TableCell>{item.itemName}</TableCell>
                                        <TableCell align="right">
                                            <Typography
                                                component="span"
                                                fontWeight={item.availableQty === 0 ? 'bold' : 'normal'}
                                                color={item.availableQty === 0 ? 'error' : 'inherit'}
                                            >
                                                {item.availableQty}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Box>
    );
};

export default StoreAdminDashboard;
